/**
 * mega-nav-click.js
 * Converts the custom mega-nav top-level item from hover-open to click-open.
 *
 * Resilience note: the trigger link (.menu-list__link) lives OUTSIDE the
 * <mega-nav-shop> custom element and is therefore NOT protected by
 * data-skip-node-update. Horizon may morph the header DOM and strip a listener
 * bound directly to the link. To survive that, we delegate from `document`
 * (which never gets re-rendered) and re-resolve the link on every event,
 * rather than holding a reference to a node that can be replaced.
 */
class MegaNavClick {
  constructor() {
    this.openItem = null; // the .menu-list__list-item currently open via click

    // Delegated click: handles both trigger toggling and outside-click close.
    document.addEventListener('click', this.onClick.bind(this), true);
    document.addEventListener('keydown', this.onKeydown.bind(this));

    // Defeat Horizon's pointer handlers: whenever aria-expanded changes on a
    // custom trigger, force it back to OUR state. Observe the whole header
    // subtree so it keeps working across re-renders.
    this.observer = new MutationObserver(this.enforce.bind(this));
    this.startObserving();
  }

  startObserving() {
    const header = document.querySelector('#header-component') || document.body;
    this.observer.observe(header, {
      attributes: true,
      attributeFilter: ['aria-expanded'],
      subtree: true,
    });
  }

  // True only for links that own a <mega-nav-shop> submenu.
  isCustomTrigger(link) {
    if (!link || !link.classList.contains('menu-list__link')) return false;
    const item = link.closest('.menu-list__list-item');
    return !!item && !!item.querySelector('mega-nav-shop');
  }

  onClick(event) {
    const link = event.target.closest('.menu-list__link');

    if (link && this.isCustomTrigger(link)) {
      event.preventDefault();
      event.stopPropagation();
      const item = link.closest('.menu-list__list-item');
      this.openItem === item ? this.close() : this.open(item, link);
      return;
    }

    // Click anywhere outside the open menu closes it.
    if (this.openItem && !this.openItem.contains(event.target)) {
      this.close();
    }
  }

  open(item, link) {
    this.openItem = item;
    link.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.openItem) return;
    const link = this.openItem.querySelector('.menu-list__link');
    if (link) link.setAttribute('aria-expanded', 'false');
    this.openItem = null;
  }

  enforce(mutations) {
    for (const m of mutations) {
      const link = m.target;
      if (!this.isCustomTrigger(link)) continue;
      const shouldBeOpen = this.openItem === link.closest('.menu-list__list-item');
      const isOpen = link.getAttribute('aria-expanded') === 'true';
      if (isOpen !== shouldBeOpen) {
        link.setAttribute('aria-expanded', String(shouldBeOpen));
      }
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.openItem) {
      const link = this.openItem.querySelector('.menu-list__link');
      this.close();
      if (link) link.focus();
    }
  }
}

function initMegaNavClick() {
  if (window.__megaNavClick) return;
  window.__megaNavClick = new MegaNavClick();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMegaNavClick);
} else {
  initMegaNavClick();
}